import { useCallback, useEffect } from "react";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { UploadSource } from "@/components/upload/types/upload-item";
import { FolderMetadataMap } from "@/types/folder-metadata";
import { WorkspaceDocumentMetadata } from "@/models/metadata/metadata";
import { Item, getAllWorkspaceItems } from "../utils/workspaceItemUtils";
import {
  getFolderPathFromPathname,
  buildPathFromNames,
} from "../utils/folderUtils";
import { useUploadStateStore } from "@/stores/upload-state.store";
import {
  DownloadedFileWithIds,
  DownloadedFolderWithIds,
} from "../providers/SharePointProvider";
import type { DownloadBatch } from "../providers/SharePointContext";
import { toast } from "@capacit-com/lara-law-shared-ui";
import Workspace from "@/models/workspace/Workspace";
import { logger } from "@/lib/logger";
import {
  checkForFileNameConflictsFromDownloaded,
  getWorkspaceFileNames,
} from "../services/conflictDetectionService";

type UseSharePointDownloadHandlerParams = {
  workspaceId: string | null;
  /** Finished downloads waiting to be uploaded, oldest first. */
  downloadQueue: DownloadBatch[];
  dequeueDownloadBatch: (batchId: string) => void;
  workspaceData: Workspace | undefined;
  isUploadInProgress: boolean;
  // Upload functions
  handleUploadFiles: (
    filesWithIds: DownloadedFileWithIds[],
    workspaceFileNames: Record<string, string>,
    folderId?: string,
    metadata?: Record<string, WorkspaceDocumentMetadata[]>
  ) => Promise<void>;
  handleFolderUpload: (
    files: FileList,
    workspace: Workspace,
    folderId?: string,
    providedMetadata?: Record<string, WorkspaceDocumentMetadata[]>,
    skipConflictCheck?: boolean,
    skipMetadataCheck?: boolean,
    fileMetadataMap?: Map<
      string,
      {
        source: UploadSource;
        driveId?: string;
        siteId?: string;
        sharepointFileId?: string;
      }
    >,
    folderMetadataMap?: FolderMetadataMap
  ) => Promise<void>;
  // Data fetchers
  ensureTenantMetadata: () => Promise<{ id?: string }[]>;
  ensureWorkspaceData: () => Promise<Workspace | null>;
};

type UseSharePointDownloadHandlerReturn = {
  createFileListFromDownloadedFiles: (
    filesWithIds: DownloadedFileWithIds[]
  ) => {
    fileList: FileList;
    metadataMap: Map<string, DownloadedFileWithIds>;
  };
  handleDownloadedFiles: (
    filesWithIds: DownloadedFileWithIds[],
    currentItems: Item[],
    folderId?: string
  ) => Promise<void>;
};

/**
 * Hook to handle SharePoint/OneDrive file downloads and automatic uploads
 */
export const useSharePointDownloadHandler = ({
  workspaceId,
  downloadQueue,
  dequeueDownloadBatch,
  workspaceData,
  isUploadInProgress,
  handleUploadFiles,
  handleFolderUpload,
  ensureTenantMetadata,
  ensureWorkspaceData,
}: UseSharePointDownloadHandlerParams): UseSharePointDownloadHandlerReturn => {
  const {
    isConflictDialogOpen,
    isUploadMetadataDialogOpen,
    setConflictingFiles,
    setIsConflictDialogOpen,
    setPendingWorkspaceFileNames,
    setPendingFolderId,
    setPendingOneDriveFiles,
    setIsUploadMetadataDialogOpen,
    setPendingMetadataFiles,
    setPendingMetadataWorkspaceFileNames,
    setPendingMetadataFolderId,
  } = useUploadStateStore();

  /**
   * Convert DownloadedFileWithIds[] to FileList with folder structure
   * Returns both FileList and metadata map for preserving SharePoint/OneDrive metadata
   */
  const createFileListFromDownloadedFiles = useCallback(
    (
      filesWithIds: DownloadedFileWithIds[]
    ): {
      fileList: FileList;
      metadataMap: Map<string, DownloadedFileWithIds>;
    } => {
      const dataTransfer = new DataTransfer();
      const metadataMap = new Map<string, DownloadedFileWithIds>();

      filesWithIds.forEach((fileWithIds) => {
        // Skip placeholder files used only to carry folder metadata
        if (fileWithIds.file.name === "__placeholder__") {
          return;
        }

        const file = fileWithIds.file;

        if (fileWithIds.folderPath) {
          const fileWithPath = new File([file], file.name, {
            type: file.type,
            lastModified: file.lastModified,
          });
          const webkitRelativePath = `${fileWithIds.folderPath}/${file.name}`;
          Object.defineProperty(fileWithPath, "webkitRelativePath", {
            writable: true,
            enumerable: true,
            configurable: true,
            value: webkitRelativePath,
          });

          Object.defineProperty(fileWithPath, "_sharePointMetadata", {
            writable: true,
            enumerable: false,
            configurable: true,
            value: {
              source: fileWithIds.source,
              driveId: fileWithIds.driveId,
              siteId: fileWithIds.siteId,
              sharepointFileId: fileWithIds.sharepointFileId,
            },
          });

          dataTransfer.items.add(fileWithPath);
          metadataMap.set(fileWithIds.folderPath, fileWithIds);
        } else {
          Object.defineProperty(file, "_sharePointMetadata", {
            writable: true,
            enumerable: false,
            configurable: true,
            value: {
              source: fileWithIds.source,
              driveId: fileWithIds.driveId,
              siteId: fileWithIds.siteId,
              sharepointFileId: fileWithIds.sharepointFileId,
            },
          });
          dataTransfer.items.add(file);
          metadataMap.set(file.name, fileWithIds);
        }
      });

      return { fileList: dataTransfer.files, metadataMap };
    },
    []
  );

  /**
   * Handler for automatically processing downloaded files from SharePoint/OneDrive
   */
  const handleDownloadedFiles = useCallback(
    async (
      filesWithIds: DownloadedFileWithIds[],
      currentItems: Item[],
      folderId?: string
    ) => {
      if (!filesWithIds || !Array.isArray(filesWithIds)) {
        logger.error("Downloaded SharePoint/OneDrive files are not a list", {
          operation: "handleDownloadedFiles",
          folderId,
        });
        return;
      }

      if (!currentItems || !Array.isArray(currentItems)) {
        logger.error("Workspace items are not a list for a SharePoint upload", {
          operation: "handleDownloadedFiles",
          folderId,
          fileCount: filesWithIds.length,
        });
        return;
      }

      const workspace = await ensureWorkspaceData().catch(() => null);
      const workspaceFileNames = getWorkspaceFileNames(
        currentItems,
        workspace || undefined,
        folderId
      );

      if (filesWithIds.length === 0) {
        return;
      }

      const hasFolderStructure = filesWithIds.some(
        (f) => f.folderPath && f.folderPath !== ""
      );

      if (hasFolderStructure) {
        if (!workspace) {
          logger.error("Upload aborted: workspace data unavailable", {
            operation: "handleDownloadedFiles",
            folderId,
            fileCount: filesWithIds.length,
          });
          showErrorToast("Workspace data mangler. Prøv at opdatere siden.");
          return;
        }

        const { fileList, metadataMap } =
          createFileListFromDownloadedFiles(filesWithIds);

        const allSharePointFolders: DownloadedFolderWithIds[] = [];
        const filesWithFoldersMetadata = filesWithIds.find(
          (f) =>
            (
              f as DownloadedFileWithIds & {
                _sharePointFolders: DownloadedFolderWithIds[];
              }
            )._sharePointFolders
        );
        if (filesWithFoldersMetadata) {
          const sharePointFolders = (
            filesWithFoldersMetadata as DownloadedFileWithIds & {
              _sharePointFolders: DownloadedFolderWithIds[];
            }
          )._sharePointFolders;
          allSharePointFolders.push(...sharePointFolders);
        }

        const fileMetadataMap = new Map<
          string,
          {
            source: UploadSource;
            driveId?: string;
            siteId?: string;
            sharepointFileId?: string;
          }
        >();
        metadataMap.forEach((fileWithIds, path) => {
          fileMetadataMap.set(path, {
            source: fileWithIds.source,
            driveId: fileWithIds.driveId,
            siteId: fileWithIds.siteId,
            sharepointFileId: fileWithIds.sharepointFileId,
          });
        });

        const folderMetadataMap: FolderMetadataMap = new Map();
        allSharePointFolders.forEach((folder) => {
          folderMetadataMap.set(folder.folderPath, {
            source: folder.source,
            driveId: folder.driveId,
            siteId: folder.siteId,
            sharepointFileId: folder.sharepointFileId,
          });
        });

        handleFolderUpload(
          fileList,
          workspace,
          folderId,
          undefined,
          false,
          false,
          fileMetadataMap,
          folderMetadataMap
        ).catch((error) => {
          logger.error("Failed to upload a folder from SharePoint/OneDrive", {
            operation: "handleDownloadedFiles",
            error,
            workspaceId: workspace.id,
            folderId,
            fileCount: filesWithIds.length,
          });
          showErrorToast("Fejl ved upload af mappe fra SharePoint/OneDrive.");
        });
      } else {
        const conflicts = checkForFileNameConflictsFromDownloaded(
          filesWithIds,
          currentItems,
          workspace || undefined,
          folderId
        );

        if (conflicts.length > 0) {
          setConflictingFiles(conflicts.map((c) => c.file));
          setPendingOneDriveFiles(filesWithIds);
          setPendingWorkspaceFileNames(workspaceFileNames);
          setPendingFolderId(folderId);
          setIsConflictDialogOpen(true);
        } else {
          const metadataList = await ensureTenantMetadata();
          if (metadataList && metadataList.length > 0) {
            setPendingOneDriveFiles(filesWithIds);
            setPendingWorkspaceFileNames(workspaceFileNames);
            setPendingFolderId(folderId);

            const metadataDataTransfer = new DataTransfer();
            filesWithIds.forEach((fileWithIds) =>
              metadataDataTransfer.items.add(fileWithIds.file)
            );

            setPendingMetadataFiles(metadataDataTransfer.files);
            setPendingMetadataWorkspaceFileNames(workspaceFileNames);
            setPendingMetadataFolderId(folderId);
            setIsUploadMetadataDialogOpen(true);
          } else {
            handleUploadFiles(filesWithIds, workspaceFileNames, folderId).catch(
              (error) => {
                logger.error("Failed to upload SharePoint/OneDrive files", {
                  operation: "handleDownloadedFiles",
                  error,
                  folderId,
                  fileCount: filesWithIds.length,
                });
                showErrorToast(
                  "Fejl ved upload af filer fra SharePoint/OneDrive."
                );
              }
            );
          }
        }
      }
    },
    [
      ensureTenantMetadata,
      ensureWorkspaceData,
      setConflictingFiles,
      setPendingOneDriveFiles,
      setPendingWorkspaceFileNames,
      setPendingFolderId,
      setIsConflictDialogOpen,
      setIsUploadMetadataDialogOpen,
      setPendingMetadataFiles,
      setPendingMetadataWorkspaceFileNames,
      setPendingMetadataFolderId,
      handleUploadFiles,
      handleFolderUpload,
      createFileListFromDownloadedFiles,
    ]
  );

  // Automatically start uploads when a queued download can be processed here.
  useEffect(() => {
    // One batch at a time. Handing a batch over does not necessarily start an
    // upload — it often opens the conflict or metadata dialog first — and those
    // keep their pending files in a single store, so starting the next batch
    // now would overwrite the one the user is still answering for.
    if (
      !workspaceData ||
      !workspaceId ||
      isUploadInProgress ||
      isConflictDialogOpen ||
      isUploadMetadataDialogOpen
    ) {
      return;
    }

    // Take the oldest batch that belongs *here*. A download started in another
    // workspace stays queued until the user goes back to it — uploading it into
    // whatever workspace they happen to be looking at would put documents in
    // front of the wrong audience. Batches for other workspaces are skipped
    // rather than blocking the queue, so a second pick made here still runs.
    const batch = downloadQueue.find(
      (candidate) =>
        !candidate.origin.workspaceId ||
        candidate.origin.workspaceId === workspaceId
    );

    if (!batch || batch.files.length === 0) {
      return;
    }

    const allItems = getAllWorkspaceItems(workspaceData);

    if (!allItems || !Array.isArray(allItems)) {
      logger.error("Workspace items are not a list for a SharePoint upload", {
        operation: "processDownloadBatch",
        workspaceId,
        fileCount: batch.files.length,
      });
      dequeueDownloadBatch(batch.id);
      return;
    }

    // Resolve the destination folder from the captured route as well, so a
    // download started in a subfolder does not land in whatever folder the
    // user happens to have open when it finishes.
    const folderNames = getFolderPathFromPathname(batch.origin.pathname);
    const currentPath = buildPathFromNames(workspaceData, folderNames);
    const folderId = currentPath[currentPath.length - 1]?.id;

    if (batch.pendingToastId !== null && batch.pendingToastId !== undefined) {
      toast.dismiss(batch.pendingToastId);
    }

    dequeueDownloadBatch(batch.id);
    handleDownloadedFiles(batch.files, allItems, folderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloadQueue,
    workspaceData,
    workspaceId,
    isUploadInProgress,
    isConflictDialogOpen,
    isUploadMetadataDialogOpen,
  ]);

  return {
    createFileListFromDownloadedFiles,
    handleDownloadedFiles,
  };
};
